/**
 * Builds repository facade — picks the right adapter (cloud vs IDB-local)
 * based on auth state + whether the API is configured.
 *
 * Why a facade not a refactor of `builds.ts`?
 *   - The IDB code is stable and ships today; we don't want to risk
 *     regressing existing builds.
 *   - Anon users + dev (no VITE_API_BASE_URL) need the IDB path to
 *     keep working unchanged.
 *   - Callers in the UI can adopt this facade progressively — the old
 *     `listBuilds()` etc. still exist.
 *
 * Migration: first time we see an authenticated user whose IDB has builds
 * and whose cloud account has none of those build names, we upload them.
 * Marker stored in localStorage so we never re-run for the same membership.
 */

import { get as idbGet } from 'idb-keyval';

import {
  cloudConfigured,
  createCloudBuild,
  deleteCloudBuild,
  getCloudBuild,
  listCloudBuilds,
  patchCloudBuild,
  setCloudShare,
  type CloudListFilter,
  type CloudSavedBuild,
} from './cloud-builds';
import {
  deleteBuild as deleteLocalBuild,
  listBuilds as listLocalBuilds,
  saveBuild as saveLocalBuild,
  type SavedBuild,
} from './builds';
import type { WizardState } from './wizard';

const MIGRATION_KEY = 'cryptarch:builds-migrated:v1';

export type RepoMode = 'cloud' | 'local';

/** Tracks the most-recent membershipId we've seen to drive cloud mode. The
 *  caller (auth handler) sets this on sign-in; callers read it implicitly
 *  through `currentMode`. Null = anon. */
let currentMembership: string | null = null;

export function setCurrentMembership(id: string | null): void {
  currentMembership = id;
}

export function currentMode(): RepoMode {
  return cloudConfigured() && currentMembership !== null ? 'cloud' : 'local';
}

// ── unified shape ────────────────────────────────────────────────────────

export interface UnifiedBuild {
  id: string;
  name: string;
  savedAt: string;
  state: SavedBuild['state'];
  /** True for cloud builds, false for local. UI uses this to gate
   *  cloud-only actions (share, like, version display). */
  isCloud: boolean;
  /** Populated for cloud only. */
  version?: number;
  shareToken?: string | null;
  isShared?: boolean;
  likeCount?: number;
  viewCount?: number;
  category?: string | null;
  description?: string;
}

function fromLocal(b: SavedBuild): UnifiedBuild {
  return { ...b, isCloud: false };
}

function fromCloud(b: CloudSavedBuild): UnifiedBuild {
  return {
    id: b.id,
    name: b.name,
    savedAt: b.savedAt,
    state: b.state,
    isCloud: true,
    version: b.version,
    shareToken: b.shareToken,
    isShared: b.isShared,
    likeCount: b.likeCount,
    viewCount: b.viewCount,
    category: b.category,
    description: b.description,
  };
}

// ── CRUD facade ──────────────────────────────────────────────────────────

export async function listBuilds(filter?: CloudListFilter): Promise<UnifiedBuild[]> {
  if (currentMode() === 'cloud') {
    const cloud = await listCloudBuilds(filter);
    return cloud.map(fromCloud);
  }
  // IDB doesn't support filtering server-side; filter client-side.
  const all = await listLocalBuilds();
  const filtered = !filter
    ? all
    : all.filter((b) => {
        if (filter.characterClass && b.state.characterClass !== filter.characterClass) return false;
        // Local builds don't have categories — only show "uncategorised"
        // when no filter is set.
        return !filter.category;
      });
  return filtered.map(fromLocal);
}

export async function getBuild(id: string): Promise<UnifiedBuild | null> {
  if (currentMode() === 'cloud') {
    const b = await getCloudBuild(id);
    return b ? fromCloud(b) : null;
  }
  const all = await listLocalBuilds();
  const hit = all.find((b) => b.id === id);
  return hit ? fromLocal(hit) : null;
}

export interface SaveOptions {
  name: string;
  description?: string;
  category?: string | null;
  /** Pulled from the wizard state if not provided. */
  subclassElement?: string | null;
  /** Pulled from the wizard state if not provided. */
  exoticHash?: number | null;
}

export async function saveBuild(state: WizardState, opts: SaveOptions): Promise<UnifiedBuild> {
  if (currentMode() === 'cloud') {
    if (!state.characterClass) throw new Error('cannot save a build without a character class');
    const created = await createCloudBuild({
      characterClass: state.characterClass,
      name: opts.name,
      description: opts.description,
      category: opts.category,
      subclassElement: opts.subclassElement ?? null,
      exoticHash: opts.exoticHash ?? state.exoticHash ?? null,
      state: stripUIBookkeeping(state),
    });
    return fromCloud(created);
  }
  const local = await saveLocalBuild(opts.name, state);
  return fromLocal(local);
}

export async function deleteBuild(id: string): Promise<void> {
  if (currentMode() === 'cloud') {
    await deleteCloudBuild(id);
    return;
  }
  await deleteLocalBuild(id);
}

/** Cloud-only ops. UI must check `build.isCloud` before calling. */
export async function patchCloudOnly(
  id: string,
  patch: { version: number; name?: string; description?: string; category?: string | null },
): Promise<UnifiedBuild> {
  if (currentMode() !== 'cloud') throw new Error('patch is cloud-only');
  const updated = await patchCloudBuild(id, patch);
  return fromCloud(updated);
}

export async function setShareCloudOnly(
  id: string,
  enabled: boolean,
): Promise<{ shareToken: string | null }> {
  if (currentMode() !== 'cloud') throw new Error('share is cloud-only');
  return setCloudShare(id, enabled);
}

// ── migration: lift IDB builds into the cloud on first sign-in ──────────

interface MigrationReport {
  uploaded: number;
  skipped: number;
}

/** Idempotent. Marks the membership as migrated so we never run twice for
 *  the same user even across devices (the marker is per-browser, but the
 *  dedupe-by-name check makes the operation safe to retry). */
export async function migrateLocalBuildsToCloud(membershipId: string): Promise<MigrationReport> {
  if (!cloudConfigured()) return { uploaded: 0, skipped: 0 };
  const marker = `${MIGRATION_KEY}:${membershipId}`;
  if (localStorage.getItem(marker)) return { uploaded: 0, skipped: 0 };

  const local = await listLocalBuilds();
  if (local.length === 0) {
    localStorage.setItem(marker, String(Date.now()));
    return { uploaded: 0, skipped: 0 };
  }

  // Pull existing cloud names so we don't upload duplicates. Comparing by
  // name is conservative — never overwrite, never delete local.
  const cloud = await listCloudBuilds();
  const cloudNames = new Set(cloud.map((b) => b.name.toLowerCase().trim()));
  let uploaded = 0;
  let skipped = 0;
  for (const b of local) {
    if (cloudNames.has(b.name.toLowerCase().trim())) {
      skipped++;
      continue;
    }
    if (!b.state.characterClass) {
      skipped++;
      continue;
    }
    try {
      await createCloudBuild({
        characterClass: b.state.characterClass,
        name: b.name,
        category: null,
        subclassElement: null,
        exoticHash: b.state.exoticHash ?? null,
        state: b.state,
      });
      uploaded++;
    } catch (err) {
      // Don't fail the whole migration on one bad build; log and continue.
      console.warn('[migrate] failed to upload', b.name, err);
      skipped++;
    }
  }
  localStorage.setItem(marker, String(Date.now()));
  return { uploaded, skipped };
}

// ── helpers ──────────────────────────────────────────────────────────────

function stripUIBookkeeping(s: WizardState): Omit<WizardState, 'step' | 'completed'> {
  // Same fields the IDB layer persists; declared explicitly to lock the
  // shape even if WizardState grows new transient fields later.
  return {
    characterId: s.characterId,
    characterClass: s.characterClass,
    exoticHash: s.exoticHash,
    exoticLocked: s.exoticLocked,
    setSelections: s.setSelections,
    targetStats: s.targetStats,
    subclassHash: s.subclassHash,
    superHash: s.superHash,
    fragmentHashes: s.fragmentHashes,
    aspectHashes: s.aspectHashes,
    abilityHashes: s.abilityHashes,
    classItemPerks: s.classItemPerks,
    fragmentStats: s.fragmentStats,
    solverOptions: s.solverOptions,
  };
}

// Quiet the linter — idbGet is here so future helpers (e.g. queue
// pending-writes when offline) have it imported.
void idbGet;
